// Must be first: seeds Lit's warning registry before any Lit-based import loads.
import 'app/client/utilities/suppressLitDevModeWarning';

import React, { useEffect } from 'react';
import {
  BrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { createRoot } from 'react-dom/client';

// Store and socket providers.
import { Provider as StoreProvider } from 'react-redux';
import createFetcher from 'app/client/store/client';
import SocketProvider from 'app/client/components/SocketProvider';
import configureStore, { getInitialState } from 'app/client/store';

// Context providers.
import BreadcrumbsDataProvider from 'app/client/components/Breadcrumbs/contexts/BreadcrumbsData';

// Components.
import ScrollToTop from 'app/client/components/ScrollToTop';

// Localization.
import 'app/client/utilities/i18n';

// WebAwesome base theme.
import '@web.awesome.me/webawesome-pro/dist/styles/themes/default.css';

Sentry.init({
  debug: false,
  enabled: __SENTRY_WEB_ENABLED__,
  dsn: __SENTRY_DSN_WEB__,
  environment: __SENTRY_ENVIRONMENT__,
  release: __SENTRY_RELEASE__,
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),

    // Replace the default breadcrumbs integration to drop the console breadcrumbs, which send
    // every `console.*` call along with the next event, unredacted. Nothing in `app/client`
    // logs to the console, so this only drops the output of the dependencies, while the
    // breadcrumbs that make a browser error readable (dom, fetch, history and xhr) are kept.
    Sentry.breadcrumbsIntegration({ console: false }),
  ],
  tracesSampleRate: 1.0,
  tunnel: '/data/telemetry',
});

// Get the initialState.
const initialState = getInitialState();

// Create a store.
const store = configureStore(initialState, createFetcher);

/**
 * This method is capable of (re)-rendering the app.
 */
const renderApp = () => {
  const Core = require('./modules/Core').default;
  const container = document.getElementById('app');
  const root = createRoot(container);

  root.render(
    <StoreProvider store={store}>
      <SocketProvider>
        <BreadcrumbsDataProvider>
          <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <ScrollToTop />
            <Core />
          </BrowserRouter>
        </BreadcrumbsDataProvider>
      </SocketProvider>
    </StoreProvider>,
  );
};

// Actually render the app.
renderApp();

// Hot Module Replacement API
if (module.hot) {
  module.hot.accept('./modules/Core', renderApp);
}
